import { type IHttpService, HttpService } from "./http-service";
import {
  type AxiosRequestConfig,
  type AxiosError,
  type AxiosResponse,
} from "axios";
import { MasterModule, ShowDialog } from "../../constant/master-actions";
import { constants } from "../../constant/constants";
import { UrlHelper } from "../url-helper-service";
import { authService } from "../../services/auth-service";

/** Share links / magic-link verify: never run MSAL refresh (would redirect to Microsoft). */
function skipAuthTokenRefreshForAnonymousContext(): boolean {
  return UrlHelper.isPublicURL() || UrlHelper.isAuthVerificationURL();
}

let isRefreshing = false;
let failedQueue: any = [];
/* ignore next because refresh token code is commented out */
function processQueue(error: any, accessToken?: string) {
  failedQueue.forEach((promise: any) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(accessToken);
    }
  });

  isRefreshing = false;
  failedQueue = [];
}

/* ignore next because refresh token code is commented out */
function isAuthError(error: AxiosError): boolean {
  if (
    error &&
    error.response != null &&
    error.response.data &&
    error.response.status === 401 &&
    !(error.response as any).isRetry
  ) {
    if (
      (error.response.data as any).message &&
      (error.response.data as any).message ==
        "Unauthorized. Access token is missing or invalid."
    ) {
      return true;
    } else if (
      typeof error.response.data == "string" &&
      error.response.data.startsWith("APIM") == false
    ) {
      return true;
    }
  }
  return false;
}

function isTooManyRequestsError(error: AxiosError<any>): boolean {
  if (
    error &&
    error.response != null &&
    error.response.data &&
    error.response.status === 429 &&
    !(error.response as any).isRetry
  ) {
    return true;
  }
  return false;
}

function isPermissionError(error: AxiosError): boolean {
  return (
    error &&
    error.response != null &&
    error.response.data &&
    error.response.status === 401 &&
    (error.response.data as any).startsWith("APIM")
  );
}

function isBadRequest(error: AxiosError): boolean {
  return error && error.response != null && error.response.status === 400;
}

function isApimSubscriptionKeyError(error: AxiosError): boolean {
  if (
    error &&
    error.response != null &&
    error.response.data &&
    error.response.status === 401
  ) {
    console.log("subscription key error");
    console.log(error);
    if (
      (error.response.data as any).message &&
      (error.response.data as any).message
        .toLowerCase()
        .includes("access denied due to invalid subscription key")
    ) {
      return true;
    }
  }
  return false;
}

function formateBadRequestResponse(error: AxiosError) {
  const lineSeprator = "<br/>";
  if (error.response) {
    console.log(error.response);
    let res = "API Status:" + error.response.statusText;
    res += lineSeprator;
    if (error.response.data && (error.response.data as any).errors) {
      res += "Validation Errors";
      res += lineSeprator;
      for (const key in (error.response.data as any).errors) {
        for (const errorIndex in (error.response.data as any).errors[key]) {
          res += (error.response.data as any).errors[key][errorIndex];
          res += lineSeprator;
        }
      }
    } else if (error.response.data) {
      if (error.response.data[""] && error.response.data[""].length > 0) {
        res += lineSeprator;
        res += error.response.data[""];
      } else if (
        (error.response.data as any).title.includes("Graph API") ||
        (error.response.data as any).detail.includes("Graph API")
      ) {
        const GraphApiReponse = (error.response.data as any).substring(
          (error.response.data as any).indexOf("{"),
          (error.response.data as any).lastIndexOf("}") + 1,
        );
        const GraphApiReponseJson = JSON.parse(GraphApiReponse);
        res += lineSeprator;
        res += GraphApiReponseJson["odata.error"].message.value;
      } else {
        res += lineSeprator;
        res += (error.response.data as any).title;
      }
    }
    return res;
  } else {
    return "API Status: BadRequest";
  }
}

/* ignore next because refresh token code is commented out */
function replaceOldToken(
  request: AxiosRequestConfig,
  accessToken: string,
): AxiosRequestConfig {
  request.headers.Authorization = `Bearer ${accessToken}`;
  return request;
}

//function redirectToSignIin() {
//    var url = window.location.href
//    var arr = url.split("/");
//    var Domain = arr[0] + "//" + arr[2];
//    window.location.href = Domain + "/" + "Session/SigIn?CurrentController=" + arr[3];
//}

export function setupResponseInterceptor(httpService: IHttpService) {
  /* ignore next */
  const onRejected = async (error: any) => {
    const originalRequest: AxiosRequestConfig = error.config;

    if (isAuthError(error)) {
      if (skipAuthTokenRefreshForAnonymousContext()) {
        return Promise.reject(error);
      }
      //Preventing token refresh call to be queued/Queue API calls if token is being refreshed
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((accessToken: any) => {
            return httpService(replaceOldToken(originalRequest, accessToken));
          })
          .catch((err: any) => {
            return Promise.reject(err);
          });
      }

      isRefreshing = true;

      return new Promise((resolve, reject) => {
        if (!httpService.store) {
          return;
        }
        //Refreshing token
        authService
          .getAccessToken()
          .then((response): any => {
            let accessToken: string = response || "";

            //Token found
            if (accessToken != null && accessToken != "") {
              //Process queued API calls
              processQueue(null, accessToken);

              //Replacing new token
              resolve(replaceOldToken(originalRequest, accessToken));
            }
            //Token refreshing failed. Reject all awaiting calls
            else {
              const responseError = new Error("Error on refreshing user token");
              (responseError as any).status = 403;
              reject(responseError);

              rejectAwaitingCallsAndRedirectToSignIn();
            }
          })
          .catch((error: any) => {
            //Token refreshing failed. Reject all awaiting calls
            console.log(error);
            const responseError = new Error("Error on refreshing user token");
            (responseError as any).status = 403;
            reject(responseError);
            rejectAwaitingCallsAndRedirectToSignIn();
          });
      }).then((r: any) => {
        r.isRetry = true;
        return httpService(r);
      });
    } else if (isTooManyRequestsError(error)) {
      if (skipAuthTokenRefreshForAnonymousContext()) {
        return Promise.reject(error);
      }
      setTimeout(() => {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          });
        }

        isRefreshing = true;

        return new Promise(async (resolve, reject) => {
          if (!httpService.store) {
            return;
          }
          //Refreshing token
          try {
            console.log("setupResponseInterceptor:TooManyRequestsError");
            let accessToken = await authService.getAccessToken();
            if (accessToken != null) {
              //Process queued API calls
              processQueue(null, accessToken);

              //Replacing new token
              resolve(replaceOldToken(originalRequest, accessToken));
            }
          } catch (e) {
            isRefreshing = false;
            console.error(error);
            const responseError = new Error("Error on refreshing user token");
            (responseError as any).status = 403;
            await rejectAwaitingCallsAndRedirectToSignIn();
            reject(responseError);
          }
        }).then((r: any) => {
          r.isRetry = true;
          return httpService(r);
        });
      }, 500);
    } else if (isApimSubscriptionKeyError(error)) {
      console.log("Signing out");
      await authService.logout();
    } else if (isPermissionError(error)) {
      let errorMessage: string =
        "Your account is not allowed to access this resource.";
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.split("-").length > 1
      ) {
        errorMessage = error.response.data.split("-")[1];
      }
    } else if (isBadRequest(error)) {
      const errorString = formateBadRequestResponse(error);
      httpService.store!.dispatch(MasterModule + ShowDialog, {
        event: function () {}, //Event which you want trigger
        messageType: "", //Type of Message  Success/Warning/Error
        prop: true, //For Dialog Show always set TRUE
        footerConstant: false, //Error handling or success message without confirmation set FALSE.
        customMesage: errorString,
        dialogHeaderText: "Error",
      });
      httpService.store!.dispatch(
        constants.apiUrl.masterModuleUrl.loaderUrl,
        false,
      );
    }
    return Promise.reject(error);
  };

  httpService.interceptors.response.use(
    (response: AxiosResponse) => response,
    onRejected,
  );
}

function rejectAwaitingCallsAndRedirectToSignIn() {
  const responseError = new Error("Error on refreshing user token");
  (responseError as any).status = 403;

  processQueue(responseError);

  if (HttpService.router) {
    alert("Unable to refresh token. Please refresh your page");
  } else UrlHelper.redirectToSignIn();
}
